import { faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import {
  getOfferItemControllerFindAllForOfferQueryKey,
  OfferImageType,
  useOfferItemControllerCreate,
  useOfferItemControllerFindAllForOffer,
  useOfferItemControllerRemove,
  useOfferItemControllerResolveDuplicates,
  useOfferItemControllerUpdate,
  useOfferItemImageControllerCreate,
  type CompanionFileDto,
  type CreateOfferItemDto,
  type OfferItemResolveDuplicatesDto,
  type ResponseOfferItemDto,
  type UpdateOfferItemDto,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import type { AwsBody } from '@uppy/aws-s3';
import type { UppyFile } from '@uppy/core';
import type { MaybeRef, MaybeRefOrGetter } from 'vue';
import { computed, ref, toValue, unref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLoading } from '../../stores/loadingStore';
import { useNotifications } from '../../stores/notificationStore';
import { useUppy, type UppyMeta } from '../form/useUppy';
import { useResponseData } from '../useResponseData';

/** Above this many items a poll transfers the whole list too often to be worth it. */
const MAX_POLLED_ITEMS = 50;

export interface UseOfferItemOptions {
  refetchOffer?: () => void;
  refetchInterval?: MaybeRefOrGetter<number | false>;
}

export const useOfferItems = (
  offerId: MaybeRef<number>,
  { refetchOffer, refetchInterval = false }: UseOfferItemOptions,
) => {
  const { watchLoading, watchRefreshing } = useLoading();
  const { addNotification } = useNotifications();
  const { t, n } = useI18n();
  const { uppy } = useUppy();

  // The count comes from a ref rather than the item list itself, because the query needs its
  // interval before the list exists.
  const polledItemCount = ref(0);
  const itemsRefetchInterval = computed(() => {
    const interval = toValue(refetchInterval);

    return interval && polledItemCount.value < MAX_POLLED_ITEMS ? interval : false;
  });
  const itemsPolling = computed(() => itemsRefetchInterval.value !== false);

  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchOfferItems,
  } = useOfferItemControllerFindAllForOffer(
    offerId,
    { limit: -1, withDeleted: true },
    {
      query: {
        refetchInterval: itemsRefetchInterval,
        queryKey: getOfferItemControllerFindAllForOfferQueryKey(offerId),
      },
    },
  );
  const itemData = useResponseData(data);
  const allItems = computed<ResponseOfferItemDto[]>(() => itemData.value?.data ?? []);
  const items = computed(() => allItems.value.filter(({ deletedAt }) => !deletedAt));
  const deletedItems = computed(() => allItems.value.filter(({ deletedAt }) => !!deletedAt));
  const includedItems = computed(() => items.value.filter((item) => item.isIncluded));

  watch(allItems, ({ length }) => (polledItemCount.value = length), { immediate: true });

  const { mutateAsync: createOfferItem, isPending: createItemLoading } =
    useOfferItemControllerCreate({
      mutation: { onSuccess: () => Promise.all([refetchOfferItems(), refetchOffer?.()]) },
    });
  const { mutateAsync: updateOfferItem, isPending: updateItemLoading } =
    useOfferItemControllerUpdate({
      mutation: { onSuccess: () => Promise.all([refetchOfferItems(), refetchOffer?.()]) },
    });
  const { mutateAsync: removeOfferItem, isPending: removeItemLoading } =
    useOfferItemControllerRemove({
      mutation: { onSuccess: () => Promise.all([refetchOfferItems(), refetchOffer?.()]) },
    });
  const { mutateAsync: resolveOfferDuplicateItems, isPending: resolveDuplicateItemsLoading } =
    useOfferItemControllerResolveDuplicates({
      mutation: { onSuccess: () => Promise.all([refetchOfferItems(), refetchOffer?.()]) },
    });
  const { mutateAsync: createOfferItemImage } = useOfferItemImageControllerCreate({
    mutation: { onSuccess: () => Promise.all([refetchOfferItems()]) },
  });

  const addItem = async (
    item: CreateOfferItemDto,
    images?: UppyFile<UppyMeta, AwsBody>[],
    showNotification = true,
    companionFiles?: Map<string, CompanionFileDto[]>,
  ) => {
    const { status, data } = await createOfferItem({ offerId: unref(offerId), data: item });

    if (status !== 201) {
      throw new Error('Failed to create offer item');
    }

    if (images?.length) await uploadItemImages(data.id, images);
    if (companionFiles?.size) await uploadCompanionItemImages(data.id, companionFiles);

    if (showNotification) {
      addNotification({
        type: 'primary',
        title: t('offer.notifications.item-added.title'),
        message: data.title,
        extra: n(data.price, 'currency'),
        image: data.cover?.imageData.displayUrls.thumb,
        actions: [
          {
            text: t('global.undo'),
            icon: faRotateLeft,
            onClick: () => removeItem(data.id, false),
          },
        ],
      });
    }
  };

  const uploadItemImages = async (offerItemId: number, images: UppyFile<UppyMeta, AwsBody>[]) => {
    const { successful, failed } = (await uppy.upload()) ?? {};
    uppy.clear();

    if (failed?.length) console.error('Failed to upload some images', failed);

    if (successful?.length) {
      await Promise.all(
        successful.map(({ uploadURL, name }) => {
          return createOfferItemImage({
            offerId: unref(offerId),
            offerItemId,
            data: {
              type: OfferImageType.SINGLE,
              imageData: { original: name, url: uploadURL },
            },
          });
        }),
      );
    }
  };

  const uploadCompanionItemImages = async (
    offerItemId: number,
    companionFiles: Map<string, CompanionFileDto[]>,
  ) => {
    await Promise.all(
      Array.from(companionFiles.entries()).flatMap(([sessionId, files]) =>
        files.map((file) =>
          Promise.all([
            createOfferItemImage({
              offerId: unref(offerId),
              offerItemId,
              data: {
                type: OfferImageType.SINGLE,
                imageData: { original: file.name, url: file.url },
              },
            }),
            // TODO: Delete used images
          ]),
        ),
      ),
    );
  };

  const updateItem = async (
    offerItemId: number,
    item: UpdateOfferItemDto,
    images?: UppyFile<UppyMeta, AwsBody>[],
    companionFiles?: Map<string, CompanionFileDto[]>,
  ) => {
    const { status, data } = await updateOfferItem({
      offerId: unref(offerId),
      itemId: offerItemId,
      data: item,
    });

    if (status !== 200) throw new Error('Failed to update offer item');

    if (images?.length) await uploadItemImages(offerItemId, images);
    if (companionFiles?.size) await uploadCompanionItemImages(offerItemId, companionFiles);
  };

  const removeItem = async (offerItemId: number, showNotification = true) => {
    const { status, data } = await removeOfferItem({
      offerId: unref(offerId),
      itemId: offerItemId,
    });

    if (status !== 200) {
      throw new Error('Failed to remove offer item');
    }

    if (showNotification) {
      addNotification({
        type: 'error',
        title: t('offer.notifications.item-removed.title'),
        message: data.title,
        extra: n(data.price, 'currency'),
        image: data.cover?.imageData.displayUrls.thumb,
      });
    }
  };

  const updateDuplicateItems = async (
    duplicateGroup: number,
    data: OfferItemResolveDuplicatesDto,
    showNotification = true,
  ) => {
    const { data: resolveResponse } = await resolveOfferDuplicateItems({
      offerId: unref(offerId),
      duplicateGroup,
      data,
    });

    if (showNotification) {
      addNotification({
        type: 'info',
        title: t('offer.notifications.duplicates-resolved.title'),
        message: t('offer.notifications.duplicates-resolved.message-with-count', {
          count: data.removeItemIds.length,
        }),
      });
    }

    return resolveResponse;
  };

  watchLoading(
    isLoading,
    createItemLoading,
    updateItemLoading,
    removeItemLoading,
    resolveDuplicateItemsLoading,
  );
  watchRefreshing(isFetching);

  return {
    items,
    deletedItems,
    includedItems,
    addItem,
    updateItem,
    removeItem,
    updateDuplicateItems,
    refetchOfferItems,
    itemsPolling,
  };
};
